import { useMemo, useState } from 'react';
import { Box, Center, Loader, Stack, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import type { CalendarEvent } from 'src/meetings/models';
import { assignSpeakerPath, type MeetingScope } from 'src/meetings/scope';
import { getUnidentifiedSpeakers } from 'src/meetings/utils/speaker-utils';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { GradientBorderedInfoCard } from 'src/common/components/card/GradientBorderedInfoCard';
import { ErrorCard } from 'src/meetings/components/ErrorCard';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { MeetingNoteContent } from './meeting-note/MeetingNoteContent';
import { StructureSelection } from './meeting-note/StructureSelection';
import { UnidentifiedSpeakersNotice } from './meeting-note/UnidentifiedSpeakersNotice';
import type { Transcript } from './transcript/types';
import { DOCUMENT_MIME_TYPES } from './meeting-note/types';

const filterDocumentFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
};

type Props = {
  event: CalendarEvent;
  transcript?: Transcript;
  scope?: MeetingScope;
};

export const MeetingNoteTab = ({ event, transcript, scope }: Props) => {
  const navigate = useNavigate();
  const [forceShowOptions, setForceShowOptions] = useState(false);

  const clientId = useMemo(() => {
    for (const participant of event.eventParticipants) {
      if (participant.participant.client) {
        return participant.participant.client.id;
      }
    }
    return undefined;
  }, [event.eventParticipants]);

  const { files: templates, isLoading: isLoadingTemplates } = useTemplates({
    templateType: 'MEETING_NOTE',
  });

  const { files: clientFiles, isLoading: isLoadingFiles } = useFileUploads({
    clientId,
  });

  const { meetingAgenda } = useCalendarEventMeetingAgenda({
    eventId: event.id,
    enabled: !!event.hasMeetingAgenda,
  });

  const agendaFiles = useMemo(() => filterDocumentFiles(clientFiles), [clientFiles]);

  const defaultTemplateId = (templates.find(template => template.isDefault) ?? templates[0])?.id;
  const defaultAgendaFileId = agendaFiles.find(file => file.file.id === meetingAgenda?.fileId)?.id;

  const {
    meetingNote,
    isLoading: isLoadingNote,
    isError,
    error,
    refetch,
  } = useCalendarEventMeetingNote(event.id, {
    enabled: event.hasTranscription,
  });

  const { generateMeetingNote, isLoading: isGenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );

  const generateForTemplate = async (templateId: string, summarise: boolean) => {
    try {
      setForceShowOptions(false);
      await generateMeetingNote({ templateId, summarise });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
      });
    }
  };

  const generateForAgenda = async (agendaFileId: string, summarise: boolean) => {
    try {
      setForceShowOptions(false);
      await generateMeetingNote({ agendaFileId, summarise });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again later', error, {
        eventId: event.id,
        agendaFileId,
      });
    }
  };

  const isLoading = isLoadingTemplates || isLoadingFiles || (event.hasMeetingNote && isLoadingNote);

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  // Speakers can only be identified where the meeting has a scope to route the identify page
  // under, so the notice is offered only when one is passed.
  const unidentifiedSpeakers =
    scope && transcript ? getUnidentifiedSpeakers(transcript.segments) : [];
  const firstUnidentifiedSpeaker = unidentifiedSpeakers[0];

  const structureSelection = (
    <StructureSelection
      templates={templates}
      defaultTemplateId={defaultTemplateId}
      agendaFiles={agendaFiles}
      defaultAgendaFileId={defaultAgendaFileId}
      isGenerating={isGenerating}
      onGenerateTemplate={generateForTemplate}
      onGenerateAgenda={generateForAgenda}
    />
  );

  const renderSelectionContent = () =>
    scope && firstUnidentifiedSpeaker ? (
      <Stack gap="md">
        <UnidentifiedSpeakersNotice
          count={unidentifiedSpeakers.length}
          onIdentify={() =>
            navigate(assignSpeakerPath(scope, event.id, firstUnidentifiedSpeaker.label))
          }
        />
        {structureSelection}
      </Stack>
    ) : (
      structureSelection
    );

  if (isError && event.hasMeetingNote) {
    return (
      <Center h="100%">
        <Stack align="center" gap="md" maw={400}>
          <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
            Unable to load meeting note
          </Text>
          <Text size="sm" c="gray.7" ta="center">
            {(error instanceof Error ? error.message : null) ||
              'An error occurred while loading the meeting note. Please contact support if this persists.'}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (meetingNote && !isGenerating) {
    if (meetingNote.status === MeetingNoteStatus.READY) {
      if (!meetingNote.markdownContent || !meetingNote.templatisedFileId) {
        return (
          <Center h="100%">
            <Stack align="center" gap="md" maw={400}>
              <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
                Meeting note data unavailable
              </Text>
              <Text size="sm" c="gray.7" ta="center">
                The meeting note appears to be incomplete. Please contact support for assistance.
              </Text>
            </Stack>
          </Center>
        );
      }

      return (
        <MeetingNoteContent
          markdownContent={meetingNote.markdownContent}
          fileId={meetingNote.templatisedFileId}
        />
      );
    }

    if (meetingNote.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING) {
      return (
        <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
          Generating the meeting note
        </GradientBorderedInfoCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.TRANSCRIPTION_PROCESSING) {
      return (
        <GradientBorderedInfoCard>
          {"Your recording is being transcribed. You'll be able to generate a note once ready."}
        </GradientBorderedInfoCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.FAILED) {
      return forceShowOptions ? (
        renderSelectionContent()
      ) : (
        <ErrorCard>
          <Text ml={'xxs'} size="sm" fw={600}>
            Unable to generate the meeting note, please{' '}
            <Text
              span
              c={'brand.8'}
              size="sm"
              fw={600}
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                if (meetingNote?.templateId) {
                  await generateForTemplate(meetingNote.templateId, true);
                }
              }}
            >
              try again
            </Text>{' '}
            or{' '}
            <Text
              span
              c={'brand.8'}
              size="sm"
              fw={600}
              style={{ cursor: 'pointer' }}
              onClick={() => setForceShowOptions(true)}
            >
              select another option
            </Text>
          </Text>
        </ErrorCard>
      );
    }

    if (meetingNote.status === MeetingNoteStatus.NOT_CREATED) {
      return renderSelectionContent();
    }
  }

  if (isGenerating) {
    return (
      <GradientBorderedInfoCard leftIcon={<Loader size="24px" />}>
        Generating the meeting note
      </GradientBorderedInfoCard>
    );
  }

  if (!event.hasTranscription) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          A transcription is required to generate a meeting note.
        </Text>
      </Box>
    );
  }

  return renderSelectionContent();
};
